import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import '../../config/api'; // ensure axios interceptors (JWT) are configured
import { Inbox, RefreshCcw, Loader2, AlertCircle, Mail, CheckCircle2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Admin › Enquiries
 *
 * What the public website's "Talk to us" form sent (website_enquiry, V689), newest first. The row is
 * the record; the e-mail to the shared inbox is only a notification, so an environment without a
 * mail relay still shows every enquiry here. Platform admin only: prospects write to us, not to a
 * tenant.
 */

interface Enquiry {
  id: number;
  received_at: string;
  full_name: string;
  email: string;
  firm: string | null;
  job_title: string | null;
  message: string;
  topic: string | null;
  page: string | null;
  status: 'NEW' | 'REPLIED' | 'CLOSED' | 'SPAM';
  notified_at: string | null;
  notify_error: string | null;
}

const STATUSES: Enquiry['status'][] = ['NEW', 'REPLIED', 'CLOSED', 'SPAM'];
const STATUS_STYLE: Record<Enquiry['status'], string> = {
  NEW: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  REPLIED: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  CLOSED: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  SPAM: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const when = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const WebsiteEnquiriesScreen: React.FC = () => {
  const [rows, setRows] = useState<Enquiry[]>([]);
  const [filter, setFilter] = useState<'' | Enquiry['status']>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get<Enquiry[]>('/api/admin/enquiries', { params: filter ? { status: filter, limit: 200 } : { limit: 200 } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not load enquiries');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id: number, status: Enquiry['status']) => {
    setBusy(id);
    try {
      await axios.patch(`/api/admin/enquiries/${id}/status`, { status });
      setRows(rs => rs.map(r => (r.id === id ? { ...r, status } : r)));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not update the enquiry');
    } finally {
      setBusy(null);
    }
  };

  const open = rows.filter(r => r.status === 'NEW').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Inbox className="w-5 h-5 text-emerald-400" /> Website enquiries</h3>
          <p className="text-xs text-slate-400 mt-1">
            Sent from the Talk to us form on axiomflowai.com. {open} awaiting a reply. Reply from the shared inbox; mark the row when you have.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value as any)}
            className="bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200">
            <option value="">All</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 border border-white/10 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-sm text-red-300">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="p-8 text-center text-slate-500 text-sm border border-dashed border-white/10 rounded-2xl">
          Nothing yet. The form posts to /api/public/enquiries and every submission appears here.
        </div>
      )}

      <div className="space-y-3">
        {rows.map(r => (
          <div key={r.id} className="p-5 bg-slate-900/60 border border-white/5 rounded-2xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold text-white">{r.full_name}</span>
                  {r.firm && <span className="text-slate-300">· {r.firm}</span>}
                  {r.job_title && <span className="text-slate-500 text-sm">({r.job_title})</span>}
                  <span className={cn('text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border', STATUS_STYLE[r.status])}>{r.status}</span>
                </div>
                <div className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <a href={`mailto:${r.email}?subject=${encodeURIComponent('Re: ' + (r.topic || 'your enquiry'))}`} className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200">
                    <Mail className="w-3 h-3" /> {r.email}
                  </a>
                  <span>{when(r.received_at)}</span>
                  {r.page && <span>page {r.page}</span>}
                  {r.topic && <span>“{r.topic}”</span>}
                  {r.notified_at
                    ? <span className="inline-flex items-center gap-1 text-slate-500"><CheckCircle2 className="w-3 h-3" /> inbox notified</span>
                    : <span className="text-amber-400/80" title={r.notify_error || ''}>not e-mailed{r.notify_error ? `: ${r.notify_error}` : ''}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {STATUSES.filter(s => s !== r.status).map(s => (
                  <button key={s} disabled={busy === r.id} onClick={() => setStatus(r.id, s)}
                    className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md border border-white/10 text-slate-400 hover:text-white hover:border-white/30 disabled:opacity-50">
                    {s === 'NEW' ? 'Reopen' : s === 'REPLIED' ? 'Replied' : s === 'CLOSED' ? 'Close' : 'Spam'}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{r.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WebsiteEnquiriesScreen;
